import { faTruck, faCalendar, faBox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { ReceptionListItem } from '../../models/Reception';
import { ReceptionStatusBadge } from './ReceptionStatusBadge';
import { DropdownActions } from '../interaction/DropdownActions';

interface ReceptionCardProps {
  reception: ReceptionListItem;
  onEdit?: (reception: ReceptionListItem) => void;
  onDelete?: (reception: ReceptionListItem) => void;
}

/**
 * Tarjeta para mostrar una recepción en vista móvil
 */
export const ReceptionCard: React.FC<ReceptionCardProps> = ({
  reception,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation('receptionsModule');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-3 sm:p-4 rounded-xl border bg-card text-card-foreground shadow hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3 gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm sm:text-base truncate">{reception.code}</h3>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground mt-1">
            <FontAwesomeIcon icon={faTruck} className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{reception.carrier}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <ReceptionStatusBadge status={reception.status} />
          <DropdownActions
            reception={reception}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <span className="text-muted-foreground">{t('table.vehicle_plate')}:</span>
          <span className="sm:ml-2 text-foreground font-medium">{reception.vehicle_plate}</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center">
          <span className="text-muted-foreground">{t('table.driver_name')}:</span>
          <span className="sm:ml-2 text-foreground font-medium">{reception.driver_name}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 pt-3 border-t border-border">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
          <div className="flex items-center gap-1">
            <FontAwesomeIcon icon={faBox} className="w-3 h-3 text-muted-foreground" />
            <span className="text-muted-foreground">{t('card.packages_label')}</span>
            <span className="font-medium text-foreground">{reception.total_qty}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">{t('card.pending_label')}</span>
            <span className="font-medium text-warning">{reception.pending_qty}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">{t('card.identified_label')}</span>
            <span className="font-medium text-success">{reception.identified_qty}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{formatDate(reception.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

export default ReceptionCard;
